// 어드민 전용 디자인 토큰: 서비스 화면(theme.ts / MUI Joy)과는 별개로 어드민에서만 쓴다.
//
// 팔레트: 브랜드 primary(#0F55A7) 를 단일 액센트로 고정하고, 뉴트럴은 쿨톤인 slate 로 통일한다
//   (theme.ts light.palette.primary.solid 와 동일: 어드민만 다른 브랜드색을 쓰지 않는다).
// radius 3단계 고정:
//   rounded-lg (8px) 컨테이너: 카드·모달·테이블 래퍼
//   rounded-md (6px) 컨트롤: 버튼·인풋·셀렉트·배지
//   rounded    (4px) 하위 요소: 세그먼트 토글의 안쪽 버튼, 체크박스, 스켈레톤 바
//                               (md 부모 안에 p-0.5 로 중첩되므로 6px - 2px = 4px 가 맞다)
// 상태색(emerald/amber/rose)은 장식이 아니라 의미 전달용이라 액센트 1개 규칙과 별개로 둔다.

export const ACCENT = "#0F55A7";
export const ACCENT_HOVER = "#0C4585";
export const ACCENT_SOFT = "#DCEFFA";

// 포커스 링: 버튼이 쓴다 (인풋은 CONTROL_BASE 가 자체 focus 스타일을 갖는다)
export const FOCUS_RING =
  "outline-none focus-visible:ring-2 focus-visible:ring-[#0F55A7]/30 focus-visible:ring-offset-1 focus-visible:ring-offset-white";

// 폼 컨트롤 공통: 테두리·색·포커스만 담는다.
// 폭(w-*)과 높이·패딩은 여기 넣지 않는다: 호출처의 className 과 같은 특이도로 충돌하면
// Tailwind 생성 순서(w-full 이 w-64 보다 뒤)에 밀려 덮어쓰기가 조용히 무시된다.
// 폭은 fullWidth 프롭, 크기는 CONTROL_SIZE 로 고른다.
export const CONTROL_BASE =
  "rounded-md border border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-[#0F55A7] focus:ring-2 focus:ring-[#0F55A7]/15 disabled:bg-slate-50 disabled:text-slate-400";

export const CONTROL_SIZE = {
  md: "h-9 px-3",
  sm: "h-7 px-2",
} as const;

// 네이티브 select 의 화살표는 Blink 에서 테두리 오른쪽 끝에 고정된다. padding-right 를 32px 로 줘도
// 화살표는 그 자리에 붙어 있고 글자와의 간격만 벌어진다. 그래서 기본 화살표를 appearance-none 으로 끄고
// chevron 을 배경 이미지로 직접 앉힌다. 오른쪽 여백은 왼쪽 padding 과 같은 값으로 맞춘다.
// px-* 와 pr-* 를 섞으면 Tailwind 생성 순서에 기대게 되므로 pl/pr 을 따로 적는다 (위 CONTROL_BASE 와 같은 이유).
export const SELECT_SIZE = {
  md: "h-9 pl-3 pr-8",
  sm: "h-7 pl-2 pr-7",
} as const;

// data URI 안에서는 currentColor 가 안 먹는다. 색을 인자로 받아 SVG 를 그때그때 만든다.
// 그래서 disabled 색도 CSS 가 아니라 selectChevron 인자로 갈라진다.
const chevronSvg = (stroke: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>`;

// inset 은 SELECT_SIZE 의 pl 과 같은 값: 좌우 여백이 어긋나 보이지 않게.
const CHEVRON_INSET: Record<keyof typeof SELECT_SIZE, string> = {
  md: "0.75rem",
  sm: "0.5rem",
};

// 평상시 slate-500, disabled 는 CONTROL_BASE 의 disabled:text-slate-400 에 맞춰 같이 흐려진다.
export const selectChevron = (
  sizing: keyof typeof SELECT_SIZE,
  disabled = false
) => ({
  backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
    chevronSvg(disabled ? "#94a3b8" : "#64748b")
  )}")`,
  backgroundRepeat: "no-repeat" as const,
  backgroundPosition: `right ${CHEVRON_INSET[sizing]} center`,
  backgroundSize: "16px 16px",
});

export const CONTROL_INVALID =
  "border-rose-400 focus:border-rose-500 focus:ring-rose-500/15";

// 숫자 열 정렬: 정산액·결제자 수처럼 자리수가 흔들리면 안 되는 값에 쓴다
export const NUMERIC = "tabular-nums";
